import { SplitFactoryProvider, useSplitClient } from '@splitsoftware/splitio-react';
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { Footer } from '#/shared/components/layout/Footer';
import { Header } from '#/shared/components/layout/Header';
import { splitConfig } from '#/shared/config/SplitConfig';
import { useAccessToken } from '#/shared/hooks/useAccessToken';
import { useFeatureFlag } from '#/shared/hooks/useFeatureFlag';
import { useFetchTimesheet } from '#/shared/hooks/useTimesheetQuery';
import { usePageStateStore } from '#/shared/stores/pageStateStore';
import { usePaidBreakExceptionStore } from '#/shared/stores/paidBreakExceptionStore';
import { useTimesheetStore } from '#/shared/stores/timesheetStore';

import { ApprovalSuccessScreen } from './components/ApprovalSuccessScreen';
import { ErrorScreen } from './components/ErrorScreen';
import { ForwardSuccessScreen } from './components/ForwardSuccessScreen';
import { LoadingScreen } from './components/LoadingScreen';
import { TimesheetReviewScreen } from './components/TimesheetReviewScreen';
import { VerificationSuccessScreen } from './components/VerificationSuccessScreen';
import { usePageStateTracking } from './hooks/usePageStateTracking';

function App() {
  const token = useAccessToken();
  const { isReady: isFeatureFlagsServiceReady } = useSplitClient();
  const initializePaidBreakExceptions = usePaidBreakExceptionStore(state => state.initializeFromTimesheet);

  const { isLoading, isError, error } = useFetchTimesheet({
    jwtToken: isFeatureFlagsServiceReady && !!token ? token : '',
  });
  const { pageState, errorCode, setPageState } = usePageStateStore(
    useShallow(state => ({
      pageState: state.pageState,
      errorCode: state.errorCode,
      setPageState: state.setPageState,
    })),
  );
  const { isEnabled: paidBreakExceptionsIsEnabled } = useFeatureFlag('PPL-2119-paid-break-exception');

  const timesheet = useTimesheetStore(state => state.timesheet);

  // Initialize paid break exceptions
  useEffect(() => {
    if (paidBreakExceptionsIsEnabled) {
      initializePaidBreakExceptions(timesheet?.shifts ?? []);
    }
  }, [timesheet?.shifts, initializePaidBreakExceptions, paidBreakExceptionsIsEnabled]);

  useEffect(() => {
    if (!token) {
      setPageState('invalid-token');
      return;
    }

    if (isLoading || !isFeatureFlagsServiceReady) {
      setPageState('pending');
      return;
    }

    if (isError || !timesheet) {
      setPageState('loading-error', error?.status);
      return;
    }

    setPageState('loading-success');
  }, [token, isLoading, isError, error?.status, timesheet, isFeatureFlagsServiceReady, setPageState]);

  // Handle page view tracking and survey triggering
  usePageStateTracking();

  const renderContent = () => {
    if (pageState === 'loading-error' || pageState === 'invalid-token') {
      return <ErrorScreen pageState={pageState} errorCode={errorCode} />;
    }

    if (pageState === 'pending') {
      return <LoadingScreen />;
    }

    if (pageState === 'submission-success') {
      return <ApprovalSuccessScreen />;
    }

    if (pageState === 'forwarding-success') {
      return <ForwardSuccessScreen />;
    }

    if (pageState === 'verification-success') {
      return <VerificationSuccessScreen />;
    }

    if (pageState === 'loading-success' && timesheet) {
      return <TimesheetReviewScreen />;
    }

    return null;
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Header />

      <main className="flex flex-1 flex-col tablet-portrait:py-xxl tablet-portrait:bg-surface-l2">
        {renderContent()}
      </main>

      <Footer />
    </div>
  );
}

function AppWithProviders() {
  return (
    <SplitFactoryProvider config={splitConfig}>
      <App />
    </SplitFactoryProvider>
  );
}

export default AppWithProviders;
